/** Plan B: existing semantic tokens and grip, without copying unbound Figma literals. */
import React, { memo } from "react";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/atoms/Button";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import CollapsibleTierHeader from "@/features/squad-planning/components/molecules/CollapsibleTierHeader";
import WatchlistOrderControls from "../WatchlistOrderControls";
import { formatPitchGpr } from "@/features/scouting/utils/watchlistPitchPresentation";
import type {
  PitchMembersListGroup,
  PitchMembersListProps,
} from "./PitchMembersListContainer";

/** Prepared groups, with local ordinals and bound movement commands. */
interface Props extends PitchMembersListProps {
  readonly groups: readonly PitchMembersListGroup[];
  /** The expanded position sections; empty while all are collapsed. */
  readonly openPositions: ReadonlySet<string>;
  /** Opens a section, or closes it if already open; others are untouched. */
  readonly onToggleGroup: (position: string) => void;
}

/**
 * The open section's rows, in their own scroller.
 * @param group - The open position group
 * @param canReorder - Whether the viewer may reorder
 * @returns The scroller holding the section's sortable rows
 */
const renderOpenRows = (group: PitchMembersListGroup, canReorder: boolean) => (
  <ScrollView
    key={group.position}
    testID={`pitch-members-rows-${group.position}`}
    className="min-h-0 shrink grow-0"
  >
    <SortableContext
      id={`members-group-${group.position}`}
      items={group.rows.map(row => row.dndId ?? row.playerId)}
      strategy={verticalListSortingStrategy}
    >
      {group.rows.map(row => (
        <Stack key={row.playerId} testID={row.testID}>
          <Stack
            testID={row.lineTestID}
            direction="horizontal"
            className="min-h-6 items-center gap-2"
          >
            <WatchlistOrderControls
              compact
              groupIds={group.ids}
              playerId={row.playerId}
              dndId={row.dndId}
              playerName={row.displayName}
              rank={row.rank}
              canEdit={canReorder}
              isFirst={row.rank === 1}
              isLast={row.rank === group.rows.length}
              onMove={row.onMove}
            />
            <Text
              variant="caption"
              className="min-w-0 flex-1 text-content-primary"
              isTruncated
            >
              {row.displayName}
            </Text>
            <Text variant="micro" className="shrink-0 text-content-muted">
              GPR {formatPitchGpr(row.gpr)}
            </Text>
            {row.trailing}
          </Stack>
          {row.status}
        </Stack>
      ))}
    </SortableContext>
  </ScrollView>
);

/**
 * One position section (SE-8638, SE-8650): its header, and — only while
 * open — its rows in their own scroller that shrinks to fit, so the headers
 * after it stay on screen however many sections are open. A collapsed
 * section mounts no SortableContext, so it contributes no drag sources. The
 * scroller is keyed by position, so a newly opened section starts at the top.
 * @param group - The prepared position group
 * @param props - The list props: open sections, toggle and reorder permission
 * @returns The section element
 */
const renderGroup = (group: PitchMembersListGroup, props: Props) => {
  const isOpen = props.openPositions.has(group.position);
  return (
    <Stack
      key={group.position}
      testID={`pitch-members-group-${group.position}`}
      className={isOpen ? "min-h-0 shrink" : "shrink-0"}
    >
      <CollapsibleTierHeader
        tierName={group.position}
        count={group.rows.length}
        isCollapsed={!isOpen}
        onToggleTier={props.onToggleGroup}
        appearance="nested"
      />
      {isOpen ? renderOpenRows(group, props.canReorder) : null}
    </Stack>
  );
};

/**
 * Shared compact rows; the surface retains its removal and save feedback.
 * @param props - Prepared groups and header actions.
 */
const PitchMembersListView = (props: Props) => (
  <Stack className="min-h-0 flex-1 px-5 pb-2 pt-4">
    <Stack
      direction="horizontal"
      className="items-center justify-between gap-2 pb-2"
    >
      <Text variant="micro-strong" className="text-content-muted">
        {props.title} · {props.count}
      </Text>
      {props.canReorder ? (
        <Text variant="micro" className="text-content-muted">
          Drag to reorder
        </Text>
      ) : null}
      {props.onAdd ? (
        <Button
          label="+ Add"
          accessibilityLabel="Add players"
          variant="link"
          size="xs"
          onPress={props.onAdd}
        />
      ) : null}
    </Stack>
    <Stack testID="pitch-members-sections" className="min-h-0 flex-1">
      {props.groups.map(group => renderGroup(group, props))}
      {props.rows.length === 0 ? (
        <Text variant="caption" className="text-content-muted">
          Add a player to see them here.
        </Text>
      ) : null}
    </Stack>
    {props.footer}
    {props.removalHint ? (
      <Text variant="micro" className="pt-2 text-content-muted">
        {props.removalHint}
      </Text>
    ) : null}
  </Stack>
);
PitchMembersListView.displayName = "PitchMembersListView";
export default memo(PitchMembersListView);
